import { Component, OnDestroy, OnInit } from "@angular/core";
import { fadeInUpAnimation } from "src/@fury/animations/fade-in-up.animation";
import { fadeInRightAnimation } from "src/@fury/animations/fade-in-right.animation";
import { scaleInAnimation } from "src/@fury/animations/scale-in.animation";
import { FormBuilder, FormGroup, FormArray, Validators } from "@angular/forms";
import { ApiService } from "src/app/services/api.service";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Location } from "@angular/common";
import { ErrorType } from "src/app/enums/error-type.enum";
import { Subject } from "rxjs";
import { finalize, takeUntil } from "rxjs/operators";
import endpoints from "src/app/helpers/endpoints";

@Component({
  selector: "fury-edit-copay-fee-settings",
  templateUrl: "./edit-copay-fee-settings.component.html",
  styleUrls: ["./edit-copay-fee-settings.component.scss"],
  animations: [fadeInUpAnimation, fadeInRightAnimation, scaleInAnimation],
})
export class EditCopayFeeSettingsComponent implements OnInit, OnDestroy {
  dynamicForm: FormGroup;
  copayFee: any[];
  loading!: boolean;
  ngUnsubscribe = new Subject<void>();
  constructor(
    public apiService: ApiService,
    public location: Location,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.dynamicForm = this.fb.group({
      feeArray: this.fb.array([]),
    });
    this.getCopaySettings();
  }

  getCopaySettings() {
    this.loading = true;
    this.apiService
      .get(endpoints.settings.copaySettings.getCopaySettings)
      .pipe(finalize(()=> this.loading = false), takeUntil(this.ngUnsubscribe))
      .subscribe(
        (res: HttpResponse<any>) => {
          if (res?.body) {
            this.copayFee = res?.body?.copayFee;
            this.populateFormArray();
          }
        },
        (err: HttpErrorResponse) => {
          if (err) {
            this.apiService.alert(err.message, ErrorType.Error);
          }
        }
      );
  }

  populateFormArray() {
    const feeArray = this.dynamicForm.get("feeArray") as FormArray;
    this.copayFee.forEach((fee) => {
      const group = this.fb.group({
        amount: [fee.amount, Validators.required],
        default: [fee.default],
        reflectedAmount: [fee.reflectedAmount, Validators.required],
        status: this.fb.group({
          monthly: this.fb.control(Boolean(fee?.status?.monthly)),
          yearly: this.fb.control(Boolean(fee?.status?.yearly)),
          twoYears: this.fb.control(Boolean(fee?.status?.twoYears)),
          threeYears: this.fb.control(Boolean(fee?.status?.threeYears)),
          fourYears: this.fb.control(Boolean(fee?.status?.fourYears)),
          fiveYears: this.fb.control(Boolean(fee?.status?.fiveYears)),
        }),
      });
      // If the current FormControl value is true, set other 'default' FormControl values to false
      group
        .get("default")
        .valueChanges.pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((value) => {
          if (value) {
            feeArray.controls.forEach((control) => {
              if (control !== group) {
                control.get("default").setValue(false, { emitEvent: false });
              }
            });
          }
        });

      feeArray.push(group);
    });
  }
  get feeArrayControls() {
    return (this.dynamicForm.get("feeArray") as FormArray).controls;
  }

  saveCopaySettings() {
    if (this.dynamicForm.valid) {
      if (!this.checkAtLeastOneStatusIsTrue()) {
        this.apiService.alert(
          "Please mark at least one copay status as true.",
          ErrorType.Error
        );
        return;
      }
      const isAtLeastOneTrue: boolean = this.isAtLeastOneDefaultTrue(
        this.dynamicForm.value?.feeArray
      );
      if (isAtLeastOneTrue) {
        const payload = {
          copayFee: this.dynamicForm.value?.feeArray,
        };
        this.apiService
          .post(endpoints.settings.copaySettings.saveCopaySettings, payload)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe(
            (res: HttpResponse<any>) => {
              if (res) {
                this.apiService.alert(res.body.message, ErrorType.Success);
              }
            },
            (err: HttpErrorResponse) => {
              this.apiService.alert(err.message, ErrorType.Error);
            }
          );
      } else {
        this.apiService.alert(
          "Please mark a Copay as default select.",
          ErrorType.Error
        );
      }
    } else {
      this.dynamicForm.markAllAsTouched();
    }
  }

  isAtLeastOneDefaultTrue(data: any) {
    return data.some((item) => item.default === true);
  }
  checkAtLeastOneStatusIsTrue() {
    const feeArray = (this.dynamicForm.get("feeArray") as FormArray).value;
    const isValid = feeArray.every((fee) =>
      Object.values(fee.status).some((value: any) => value === true)
    );
    return isValid;
  }
  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
